import type { IncidentDetailsDtoBase } from '@/gen/types/IncidentDetailsDtoBase';
import { defaultIncidentAudience, type IncidentAudience } from '@/incidents/forms/audience';
import { resolveIncidentType } from '@/incidents/forms/registry';
import { getIncidentFieldRules } from '@/incidents/forms/rules';
import { resolveIncidentRichText } from '@/lib/incident-rich-text';
import type { IncidentRichText } from '@/lib/jira-wiki';
import { humanizeIdentifier, isIdentifierLikeString } from '@/lib/string-format';
import { formatDateTime, formatDateToISOString } from '@/stores/helpers';

export interface IncidentDetailField {
    key: string;
    label: string;
    value: IncidentRichText;
}

const DETAIL_FIELDS_EXCLUDED_ROOTS = new Set([
    'summary',
    'description',
    'comments',
    'status',
    'reporterName',
    'createdAt',
    'incidentId',
    'type',
    'externalReference',
    'attachments',
]);

// A communication ticket is a restricted, customer-facing view of a master incident. It surfaces
// ONLY these detail fields (matched on the exact flattened key); the master's other fields must
// never be rendered. "Affected location" is the scope only — not the sites or the project name
// Classification mirrors a normal incident: category/sub-category come from the type in the meta
// sidebar, so there is no sub-category row here. (Priority and status are also in the sidebar;
// summary is the title.)
const COMMUNICATION_DETAIL_FIELD_KEYS = new Set([
    'occurredAt',
    'location.scope',
]);

// Customer-facing (regular) incident view only: these location facts are now rendered by the
// affected-locations tree in the sidebar, so the inline detail rows for them are redundant. The
// communication view still surfaces location.sites as its "Affected location" (see above), so this
// exclusion is applied only on the non-communication path.
const REGULAR_DETAIL_FIELD_EXCLUDED_KEYS = new Set([
    'location.sites',
    'location.locationIds',
]);

const RULE_FALLBACK_ORDER_BASE = 9_000;
const UNMAPPED_ORDER_BASE = 10_000;

function formatDetailLabel(key: string) {
    return humanizeIdentifier(key, { casing: 'title' })
        .replace(/\bS L A\b/g, 'SLA')
        .replace(/\bH V A C\b/g, 'HVAC')
        .replace(/\bI T\b/g, 'IT');
}

function isIsoDateString(value: string) {
    if (!value) return false;
    if (!/^\d{4}-\d{2}-\d{2}T/.test(value)) return false;
    return toIsoDateString(value) !== null;
}

function toIsoDateString(value: string): string | null {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return formatDateToISOString(parsedDate);
}

function toPlainRichText(text: string): IncidentRichText {
    return {
        text,
        renderAsHtml: false,
    };
}

function combineRichTextValues(values: IncidentRichText[], separator: string): IncidentRichText {
    const nonEmptyValues = values.filter((value) => value.text.length > 0);
    if (nonEmptyValues.length === 0) return toPlainRichText('');
    if (nonEmptyValues.every((value) => !value.renderAsHtml)) {
        return toPlainRichText(nonEmptyValues.map((value) => value.text).join(separator));
    }

    const normalizedText = nonEmptyValues
        .map((value) => (value.renderAsHtml ? value.text : value.text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')))
        .join('<br>');

    return {
        text: normalizedText,
        renderAsHtml: true,
    };
}

function formatDetailValue(fieldKey: string, value: unknown): IncidentRichText {
    if (value === null || value === undefined) return toPlainRichText('');

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return toPlainRichText('');
        const isoDateString = isIsoDateString(trimmed) ? toIsoDateString(trimmed) : null;
        if (isoDateString) return toPlainRichText(formatDateTime(isoDateString));

        const enumLikeValue = isIdentifierLikeString(trimmed);
        if (enumLikeValue) return toPlainRichText(humanizeIdentifier(trimmed, { casing: 'title' }));

        return resolveIncidentRichText(trimmed);
    }

    if (typeof value === 'number' || typeof value === 'bigint') return toPlainRichText(String(value));
    if (typeof value === 'boolean') return toPlainRichText(value ? 'Yes' : 'No');

    if (Array.isArray(value)) {
        return combineRichTextValues(
            value.map((item) => formatDetailValue(fieldKey, item)),
            ', ',
        );
    }

    if (isPlainObject(value)) {
        if (typeof value.fileName === 'string' && value.fileName.trim()) {
            return toPlainRichText(value.fileName.trim());
        }
        if (typeof value.label === 'string' && value.label.trim()) {
            return toPlainRichText(value.label.trim());
        }
        if (typeof value.value === 'string' && value.value.trim()) {
            return toPlainRichText(humanizeIdentifier(value.value.trim(), { casing: 'title' }));
        }

        return combineRichTextValues(
            Object.entries(value).map(([nestedKey, nestedValue]) => {
                const formatted = formatDetailValue(nestedKey, nestedValue);
                if (!formatted.text) return toPlainRichText('');
                return formatted.renderAsHtml
                    ? {
                          text: `<strong>${formatDetailLabel(nestedKey)}:</strong> ${formatted.text}`,
                          renderAsHtml: true,
                      }
                    : toPlainRichText(`${formatDetailLabel(nestedKey)}: ${formatted.text}`);
            }),
            ', ',
        );
    }

    return toPlainRichText('');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return Object.prototype.toString.call(value) === '[object Object]';
}

function shouldFlattenObject(value: Record<string, unknown>) {
    if ('fileName' in value || 'label' in value || 'value' in value) return false;
    return true;
}

function flattenDetailEntries(
    value: Record<string, unknown>,
    prefix = '',
    entries: Array<{ key: string; value: unknown }> = [],
) {
    for (const [key, nestedValue] of Object.entries(value)) {
        const path = prefix ? `${prefix}.${key}` : key;

        if (isPlainObject(nestedValue) && shouldFlattenObject(nestedValue)) {
            flattenDetailEntries(nestedValue, path, entries);
            continue;
        }

        entries.push({ key: path, value: nestedValue });
    }

    return entries;
}

function isExcludedFieldKey(key: string, audience: IncidentAudience) {
    const root = key.split('.')[0];
    return DETAIL_FIELDS_EXCLUDED_ROOTS.has(root) || (audience === 'customer' && root === 'reportedByFunction');
}

function getRuleMetadata(type: IncidentDetailsDtoBase['type']) {
    const incidentType = resolveIncidentType(type);
    if (!incidentType) return new Map<string, { label: string; order: number; index: number }>();

    const rules = getIncidentFieldRules(incidentType);
    const map = new Map<string, { label: string; order: number; index: number }>();

    for (const [index, rule] of rules.entries()) {
        map.set(rule.key, {
            label: rule.label?.trim() || formatDetailLabel(rule.key),
            order: typeof rule.order === 'number' ? rule.order : RULE_FALLBACK_ORDER_BASE + index,
            index,
        });
    }

    return map;
}

export function buildIncidentDetailFields(
    details: IncidentDetailsDtoBase,
    audience: IncidentAudience = defaultIncidentAudience,
): IncidentDetailField[] {
    const rawDetails = details as unknown as Record<string, unknown>;
    const isCommunication = rawDetails.isCommunication === true;
    const ruleByKey = getRuleMetadata(details.type);

    return flattenDetailEntries(rawDetails)
        .filter((entry) =>
            isCommunication
                ? COMMUNICATION_DETAIL_FIELD_KEYS.has(entry.key)
                : !isExcludedFieldKey(entry.key, audience) && !REGULAR_DETAIL_FIELD_EXCLUDED_KEYS.has(entry.key),
        )
        .map((entry, sourceIndex) => {
            const formattedValue = formatDetailValue(entry.key, entry.value);
            if (!formattedValue.text) return null;

            const rule = ruleByKey.get(entry.key);

            return {
                key: entry.key,
                label: rule?.label ?? formatDetailLabel(entry.key),
                value: formattedValue,
                sortOrder: rule?.order ?? UNMAPPED_ORDER_BASE + sourceIndex,
                ruleIndex: rule?.index ?? Number.POSITIVE_INFINITY,
                sourceIndex,
            };
        })
        .filter(
            (field): field is IncidentDetailField & { sortOrder: number; ruleIndex: number; sourceIndex: number } =>
                field !== null,
        )
        .sort((a, b) => {
            if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
            if (a.ruleIndex !== b.ruleIndex) return a.ruleIndex - b.ruleIndex;
            return a.sourceIndex - b.sourceIndex;
        })
        .map(({ key, label, value }) => ({
            key,
            label,
            value,
        }));
}
